import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, RefreshCw, ShieldCheck, AlertCircle, CheckCircle } from 'lucide-react';
import { transactionsApi } from '../../../api/transactions.api';
import { useTranslation } from 'react-i18next';

const PayPalPayment: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { amount, accountCode } = location.state || { amount: '0', accountCode: '' };

    const [loading, setLoading] = useState(true);
    const [approvalUrl, setApprovalUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        createPayPalOrder();
    }, []);

    const createPayPalOrder = async () => {
        try {
            setLoading(true);
            setError(null);

            // Validate data before sending
            if (!accountCode) {
                throw new Error(t('services:payment.error'));
            }
            if (!amount || parseFloat(amount) <= 0) {
                throw new Error(t('services:depot.invalidAmount'));
            }

            console.log('[PayPal] Creating order:', { compte: accountCode, montant: parseFloat(amount) });

            // Call PayPal create-order API
            const response = await transactionsApi.createPayPalOrder({
                compte: accountCode,
                montant: parseFloat(amount),
                description: 'Dépôt Ti Kanè Bousanm via PayPal',
            });

            if (response.approval_url) {
                setApprovalUrl(response.approval_url);
            } else {
                throw new Error(t('services:payment.error'));
            }
        } catch (err: any) {
            console.error('PayPal order error:', err);
            // Extract error message from API response
            const errorMessage = err.response?.data?.error || err.message || t('common:error');
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleRetry = () => {
        setError(null);
        createPayPalOrder();
    };

    const [showConfirmClose, setShowConfirmClose] = useState(false);

    const handleClose = () => {
        setShowConfirmClose(true);
    };

    const confirmClose = () => {
        setShowConfirmClose(false);
        navigate(-1);
    };

    // PayPal icon SVG
    const PayPalIcon = () => (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.067 8.478c.492.315.844.825.983 1.39a3.2 3.2 0 01-.145 1.967l-1.78 5.89c-.19.63-.63 1.163-1.223 1.478a2.96 2.96 0 01-1.518.395H9.89l-.478 2.004A.99.99 0 018.44 22H5.993a1 1 0 01-.976-1.221l2.512-10.506a1 1 0 01.977-.773h4.442c1.428 0 2.788.615 3.723 1.676.22.25.413.522.577.812.41-.038.83.016 1.226.157.555.198 1.037.542 1.393 1.01.357.467.58 1.032.648 1.624.069.592-.008 1.195-.223 1.745-.214.55-.552 1.032-.98 1.395a3.163 3.163 0 01-1.245.742z" />
        </svg>
    );

    // Loading State
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#0070BA] to-[#003087] text-white">
                    <div className="max-w-4xl mx-auto px-4 py-4">
                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => navigate(-1)}
                                className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <div className="flex items-center gap-3">
                                <PayPalIcon />
                                <span className="text-lg font-bold">PayPal</span>
                            </div>
                            <div className="w-10" />
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
                    <div className="w-12 h-12 border-4 border-[#0070BA] border-t-transparent rounded-full animate-spin mb-6" />
                    <p className="text-lg font-medium text-gray-800">{t('services:payment.preparing')}</p>
                    <p className="text-sm text-gray-500 mt-2">
                        {t('services:detailsPage.amount')}: ${parseFloat(amount).toFixed(2)} USD
                    </p>
                </div>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#0070BA] to-[#003087] text-white">
                    <div className="max-w-4xl mx-auto px-4 py-4">
                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => navigate(-1)}
                                className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <div className="flex items-center gap-3">
                                <PayPalIcon />
                                <span className="text-lg font-bold">PayPal</span>
                            </div>
                            <div className="w-10" />
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
                    <AlertCircle className="w-16 h-16 text-red-500 mb-6" />
                    <h2 className="text-2xl font-bold text-red-500 mb-2">{t('services:payment.error')}</h2>
                    <p className="text-gray-600 text-center mb-8 max-w-md">{error}</p>

                    <button
                        onClick={handleRetry}
                        className="flex items-center gap-2 bg-[#0070BA] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#005B95] transition-colors mb-4"
                    >
                        <RefreshCw className="w-5 h-5" />
                        <span>{t('services:payment.retry')}</span>
                    </button>

                    <button
                        onClick={() => navigate(-1)}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        {t('services:payment.back')}
                    </button>
                </div>
            </div>
        );
    }

    // Success State
    if (showSuccess) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
                <div className="bg-white rounded-3xl p-10 shadow-2xl text-center max-w-md w-full animate-scale-in">
                    <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-6" />
                    <h2 className="text-2xl font-bold text-emerald-500 mb-2">{t('services:payment.success.title')}</h2>
                    <p className="text-gray-600 mb-6">{t('services:payment.success.message')}</p>
                    <p className="text-3xl font-bold text-gray-800 mb-6">
                        ${parseFloat(amount).toFixed(2)} USD
                    </p>
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="w-4 h-4 border-2 border-[#0070BA] border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm">{t('services:payment.returnToHome')}</span>
                    </div>
                </div>

                <style>{`
                    @keyframes scale-in {
                        from { transform: scale(0.9); opacity: 0; }
                        to { transform: scale(1); opacity: 1; }
                    }
                    .animate-scale-in {
                        animation: scale-in 0.3s ease-out;
                    }
                `}</style>
            </div>
        );
    }

    // WebView/iframe for PayPal
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0070BA] to-[#003087] text-white">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={handleClose}
                            className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-3">
                            <PayPalIcon />
                            <span className="text-lg font-bold">{t('services:payment.secure', { provider: 'PayPal' })}</span>
                        </div>
                        <div className="bg-white/25 px-3 py-1.5 rounded-xl">
                            <span className="text-sm font-bold">${parseFloat(amount).toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* PayPal iframe */}
            {approvalUrl && (
                <div className="flex-1">
                    <iframe
                        src={approvalUrl}
                        className="w-full h-full min-h-[600px] border-0"
                        title="PayPal Payment"
                        sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                    />
                </div>
            )}

            {/* Footer */}
            <div className="bg-white/90 backdrop-blur-md border-t border-gray-200 py-3 px-4">
                <div className="flex items-center justify-center gap-2 text-gray-500">
                    <ShieldCheck className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm">{t('services:payment.secure', { provider: 'PayPal' })}</span>
                </div>
            </div>

            {/* Confirm Close Modal */}
            {showConfirmClose && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-center justify-center">
                    <div className="bg-white rounded-t-2xl md:rounded-2xl p-6 w-full md:max-w-md animate-slide-up">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                                <AlertCircle className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">{t('services:payment.confirmClose.title')}</h3>
                                <p className="text-sm text-gray-500">{t('services:payment.confirmClose.subtitle')}</p>
                            </div>
                        </div>
                        <p className="text-gray-600 mb-6">
                            {t('services:payment.confirmClose.message')}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowConfirmClose(false)}
                                className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                {t('services:payment.confirmClose.continue')}
                            </button>
                            <button
                                onClick={confirmClose}
                                className="flex-1 py-3 px-4 rounded-xl bg-red-500 font-semibold text-white hover:bg-red-600 transition-colors"
                            >
                                {t('services:payment.confirmClose.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slide-up {
                    from { transform: translateY(100%); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .animate-slide-up {
                    animation: slide-up 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default PayPalPayment;
